import React, {useMemo} from 'react';

/**
 * El texto de un bloque de Informes, en sólo lectura: portal del cliente, vista previa y PDF.
 *
 * El HTML ya viene saneado por el backend (`reports/services/text_block.py`), pero acá NO se usa
 * `dangerouslySetInnerHTML`: lo lee el cliente de la agencia, y la regla del portal es que el texto
 * de un tercero va como elementos de React (`portal-cliente.md`). Se parsea con `DOMParser` —que no
 * ejecuta nada— y se arma el árbol con la MISMA lista corta del backend. Cualquier otra etiqueta
 * deja sólo su texto, y las que pueden traer código o contenido embebido se descartan enteras.
 * Es la segunda capa: si el saneado del backend fallara, esto sigue sin dibujar nada peligroso.
 */

const TAGS = {
    P: 'p', DIV: 'p', H1: 'p', H2: 'p', H3: 'p', H4: 'p', H5: 'p', H6: 'p', BLOCKQUOTE: 'p',
    BR: 'br',
    STRONG: 'strong', B: 'strong',
    EM: 'em', I: 'em',
    UL: 'ul', OL: 'ol', LI: 'li',
    A: 'a',
};

// Se descartan con todo su contenido: dejar «sólo el texto» de un <script> mostraría el código.
const DROPPED = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'SVG',
    'MATH', 'IMG', 'VIDEO', 'AUDIO', 'CANVAS', 'FORM', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'HEAD',
    'TITLE', 'META', 'LINK', 'BASE']);

/** El href, sólo si es http, https o mailto. */
export function safeHref(href) {
    const value = (href || '').trim();
    return /^(https?:\/\/|mailto:)/i.test(value) ? value : null;
}

function toReact(node, key) {
    if (node.nodeType === 3) return node.nodeValue; // texto: React lo escapa
    if (node.nodeType !== 1) return null; // comentarios, etc.
    const name = node.tagName.toUpperCase();
    if (DROPPED.has(name)) return null;
    const children = Array.from(node.childNodes).map((child, index) => toReact(child, index));
    const tag = TAGS[name];
    if (!tag) return <React.Fragment key={key}>{children}</React.Fragment>;
    if (tag === 'br') return <br key={key}/>;
    if (tag === 'a') {
        const href = safeHref(node.getAttribute('href'));
        if (!href) return <React.Fragment key={key}>{children}</React.Fragment>;
        return <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">{children}</a>;
    }
    return React.createElement(tag, {key}, children);
}

/** El árbol de React para un HTML. Exportado para los tests. */
export function richTextNodes(html) {
    if (!html || typeof html !== 'string' || typeof DOMParser === 'undefined') return [];
    const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}</body>`, 'text/html');
    return Array.from(doc.body.childNodes).map((node, index) => toReact(node, index));
}

export function RichTextView({html, className = ''}) {
    const nodes = useMemo(() => richTextNodes(html), [html]);
    return <div className={`inf-text-view ${className}`.trim()} data-testid="informes-text-view">{nodes}</div>;
}
